import {
  FORM_FIELD_TO_API_NAME,
  FormFieldApiName,
  MARKETING_CONSENT,
  MarketingConsent,
  UNSUBSCRIBE_FORM_TYPE,
} from './constants';
import { AttributionApiName, FormAttribution } from './formAttribution';
import { FormFieldValue } from '../Form.types';
import type { Locale } from '../../../types';

/**
 * Client-side context that is neither a form field nor attribution. Each key names its own
 * provenance (app vs browser vs user) so the container can grow without another rename.
 */
export type ClientContext = {
  /** The site locale the form was rendered in (Next's route locale: en-US, ja, ko, zh-CN). */
  app_language: string;
  /** navigator.language — the browser's preferred language, which may differ from the site's. */
  browser_language: string;
  user_timezone: string;
};

/**
 * Every key is named, rather than an index signature: the type is constructible by callers and
 * honest about which keys are scalars — the user-entered fields (a subset per form type), the
 * always-present attribution and consent strings, one picklist value, and one structured object.
 */
export type FormSubmissionPayload = Partial<Record<FormFieldApiName, string>> &
  Record<AttributionApiName | 'Submission_URL__c' | 'Form_ID__c' | 'CampaignId' | 'Consent_Timestamp__c', string> & {
    Marketing_Consent__c: MarketingConsent;
    client_context: ClientContext;
  };

type BuildFormSubmissionPayloadArgs = {
  formValues: Record<string, FormFieldValue>;
  formType?: string;
  /** The site locale the form rendered in; undefined outside a Next router (Storybook, tests). */
  locale?: Locale;
  /** Whether the marketing opt-in checkbox was checked; false when no checkbox rendered. */
  marketingOptIn: boolean;
  attribution: FormAttribution;
};

/**
 * Submitting the unsubscribe form is the opt-out. Everywhere else the checkbox is required, so a
 * submission that passed validation has it checked — unless no checkbox rendered at all (a draft
 * entry without a disclaimer), which records nothing rather than a false opt-out.
 */
const resolveMarketingConsent = (formType: string | undefined, marketingOptIn: boolean): MarketingConsent => {
  if (formType === UNSUBSCRIBE_FORM_TYPE) return MARKETING_CONSENT.optedOut;
  return marketingOptIn ? MARKETING_CONSENT.optedIn : MARKETING_CONSENT.notSet;
};

const getClientContext = (locale?: Locale): ClientContext => {
  let userTimezone = '';
  try {
    userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    // Best-effort metadata — some embedded engines throw resolving the timezone.
  }
  return {
    app_language: locale ?? '',
    browser_language: typeof navigator !== 'undefined' ? navigator.language : '',
    user_timezone: userTimezone,
  };
};

export const buildFormSubmissionPayload = ({
  formValues,
  formType,
  locale,
  marketingOptIn,
  attribution,
}: BuildFormSubmissionPayloadArgs): FormSubmissionPayload => {
  // `type` is required in Contentful, so it is always the identifier here.
  const formIdentifier = formType || '';
  const fieldValues: Partial<Record<FormFieldApiName, string>> = {};

  Object.entries(formValues).forEach(([formKey, value]) => {
    const apiName = FORM_FIELD_TO_API_NAME[formKey];
    if (apiName) fieldValues[apiName] = value;
  });

  return {
    ...fieldValues,
    // Attribution keys are always present, '' when the visit carried no value. Listed one by
    // one so adding a parameter to ATTRIBUTION_QUERY_PARAMS is a compile error here until the
    // payload sends it too.
    UTM_Sourcev1__c: attribution.UTM_Sourcev1__c || '',
    UTM_Mediumv1__c: attribution.UTM_Mediumv1__c || '',
    UTM_Campaignv1__c: attribution.UTM_Campaignv1__c || '',
    UTM_Contentv1__c: attribution.UTM_Contentv1__c || '',
    UTM_Termv1__c: attribution.UTM_Termv1__c || '',
    GCLID__c: attribution.GCLID__c || '',
    Referrer_URL__c: attribution.Referrer_URL__c || '',
    Submission_URL__c: typeof window !== 'undefined' ? window.location.href : '',
    Form_ID__c: formIdentifier,
    CampaignId: formIdentifier,
    // The SFDC picklist replaced the original Privacy_Policy_Consent__c boolean (#952). The
    // timestamp records when the value was set whichever it is — a revocation needs stamping
    // as much as a grant does.
    Marketing_Consent__c: resolveMarketingConsent(formType, marketingOptIn),
    Consent_Timestamp__c: new Date().toISOString(),
    client_context: getClientContext(locale),
  };
};
